// TrendChartSection Component
// Handles the display and interaction of trend charts for different data types (temperature, power, delta)
// Manages sensor selection, metric selection, and trendline controls
import React from 'react';
import { ChartCard } from "@/components/chart-card";
import { TrendlineControls } from "./TrendlineControls";
import { ApiPath } from "@/api/path";
import { TSMetric } from "@/query/query";
import { AUTO } from "@/dateRanges/windowSizes";
import { calculateDeltaTemperatures } from "@/utils/deltaCalculations";
import { ChartOptionsContextProvider } from "@/chartOptions/ChartOptionsContext";
import ChartOptionsSelector from "@/chartOptions/chartOptionsSelector";
import QuickTooltip from "@/components/quick-tooltip";
import { Info } from "lucide-react";

// Props interface for the TrendChartSection component
interface TrendChartSectionProps {
  title: string;  // Chart title
  chartType: 'temperature' | 'power' | 'delta';  // Type of data being displayed
  selectedSensor: string;  // Currently selected sensor
  setSelectedSensor: (value: string) => void;  // Function to update selected sensor
  selectedMetric: string;  // Currently selected metric
  setSelectedMetric: (value: string) => void;  // Function to update selected metric
  availableSensors: string[];  // List of available sensors for this chart type
  availableMetrics: string[];  // List of available metrics for the selected sensor
  trendlines: Array<{  // Current trendlines being displayed
    sensor: string;
    metric: string;
    data: any;
    chartType: 'temperature' | 'power' | 'delta';
  }>;
  onAddTrendline: () => void;  // Function to add a new trendline
  onRemoveTrendline: (index: number) => void;  // Function to remove a trendline
  selectedAssetIds: string[];  // IDs of selected assets
  startDate: string;  // Start date for data range
  endDate: string;  // End date for data range
  results: any;  // Analysis results data
}

export function TrendChartSection({
  title,
  chartType,
  selectedSensor,
  setSelectedSensor,
  selectedMetric,
  setSelectedMetric,
  availableSensors,
  availableMetrics,
  trendlines,
  onAddTrendline,
  onRemoveTrendline,
  selectedAssetIds,
  startDate,
  endDate,
  results
}: TrendChartSectionProps) {
  // Function to get the appropriate query based on chart type
  const getQuery = () => {
    switch (chartType) {
      case 'power':
        return [{
          metrics: [TSMetric.ALL_PHASE_POWERS],
          windowSize: AUTO,
          dateRange: {
            label: "Custom Range",
            value: {
              from: () => new Date(startDate),
              to: () => new Date(endDate),
              live: false
            }
          },
          installation_id: selectedAssetIds,
        }];
      case 'delta':
      case 'temperature':
        return [{
          metrics: [TSMetric.ALL_TEMPERATURES],
          windowSize: AUTO,
          dateRange: {
            label: "Custom Range",
            value: {
              from: () => new Date(startDate),
              to: () => new Date(endDate),
              live: false
            }
          },
          installation_id: selectedAssetIds,
          target_unit: 'degC',
        }];
    }
  };

  // Function to combine series data based on chart type
  const getCombineSeries = () => {
    if (chartType === 'power') {
      // For power charts, calculate total power by summing all phases
      return (series: Array<{ name: string; data: Array<{ timestamp: string; value: number }> }>) => {
        if (!series || series.length === 0 || !series[0] || !series[0].data) {
          return [];
        }

        // Calculate total power by summing all phases at each timestamp
        const totalPowerData = series[0].data.map((_, index) => {
          const total = series.reduce((sum, s) => {
            const value = s.data[index]?.value || 0;
            return sum + value;
          }, 0);
          return {
            timestamp: series[0].data[index].timestamp,
            value: total
          };
        });

        // Add total power as a new series
        return [
          ...series,
          {
            name: "Total Power",
            data: totalPowerData,
            type: 'line',
            lineStyle: { 
              color: "#007DC2",
              width: 3
            }
          }
        ];
      };
    }

    if (chartType === 'delta') {
      // For delta charts, calculate temperature differences
      return (series: Array<{ name: string; data: Array<{ timestamp: string; value: number }> }>) => {
        if (!series || series.length === 0 || !series[0] || !series[0].data) {
          return [];
        }

        // Calculate delta temperatures
        const deltaData = calculateDeltaTemperatures(series.reduce((acc: Record<string, number[]>, s) => {
          acc[s.name] = s.data.map(d => d.value);
          return acc;
        }, {}));
        
        // Only return series that start with DELTA_ and have data
        const deltaSeries = Object.entries(deltaData)
          .filter(([key]) => key.startsWith('DELTA_'))
          .map(([key, values]) => ({
            name: key,
            data: values.map((value: number, i: number) => ({
              timestamp: series[0].data[i].timestamp,
              value
            }))
          }))
          .filter(series => series.data.length > 0);

        // Convert delta temperatures if needed
        return deltaSeries;
      };
    }

    // For temperature charts, return data as-is (already in Celsius)
    if (chartType === 'temperature') {
      return (series: Array<{ name: string; data: Array<{ timestamp: string; value: number }> }>) => series;
    }

    return undefined;
  };

  // Convert trendline data for temperature and delta charts
  const convertedTrendlineData = (chartType === 'temperature' || chartType === 'delta') 
    ? trendlines.filter(t => t.chartType === chartType)
    : trendlines.filter(t => t.chartType === chartType);

  return (
    <div>
      {/* Chart options context provider for date range and other settings */}
      <ChartOptionsContextProvider
        customDateRange={{
          label: "Custom Range",
          value: {
            from: () => new Date(startDate),
            to: () => new Date(endDate),
            live: false
          }
        }}
        outdoorTemperatureUnit="degC"
      >
        <TrendChartContent
          title={title}
          chartType={chartType}
          selectedSensor={selectedSensor}
          setSelectedSensor={setSelectedSensor}
          selectedMetric={selectedMetric}
          setSelectedMetric={setSelectedMetric}
          availableSensors={availableSensors}
          availableMetrics={availableMetrics}
          trendlines={trendlines}
          onAddTrendline={onAddTrendline}
          onRemoveTrendline={onRemoveTrendline}
          selectedAssetIds={selectedAssetIds}
          startDate={startDate}
          endDate={endDate}
          results={results}
          getQuery={getQuery}
          getCombineSeries={getCombineSeries}
          convertedTrendlineData={convertedTrendlineData}
        />
      </ChartOptionsContextProvider>
    </div>
  );
}

// Inner component that can access the chart options context
function TrendChartContent({
  title,
  chartType,
  selectedSensor,
  setSelectedSensor,
  selectedMetric,
  setSelectedMetric,
  availableSensors,
  availableMetrics,
  trendlines,
  onAddTrendline,
  onRemoveTrendline,
  selectedAssetIds,
  startDate,
  endDate,
  results,
  getQuery,
  getCombineSeries,
  convertedTrendlineData
}: TrendChartSectionProps & {
  getQuery: () => any;
  getCombineSeries: () => any;
  convertedTrendlineData: any;
}) {
  return (
    <>
      {/* Chart header with title and controls */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold">{title}</h2>
          {/* Info icon for delta chart explaining what each DELTA represents */}
          {chartType === 'delta' && (
            <QuickTooltip text="DELTA_COMP = Compressor outlet - Compressor inlet, DELTA_COIL = Vapor liquid coil outlet - Vapor liquid coil, DELTA_COOLING = Compressor inlet - Vapor liquid coil outlet, DELTA_AMBIENT_OFFSET = Ambient - Compressor inlet">
              <Info className="h-4 w-4 text-gray-500 hover:text-gray-700 cursor-help" />
            </QuickTooltip>
          )}
        </div>
        <div className="flex items-center gap-4">
          <ChartOptionsSelector />
          <div className="flex items-center gap-2">
            <TrendlineControls
              chartType={chartType}
              selectedSensor={selectedSensor}
              setSelectedSensor={setSelectedSensor}
              selectedMetric={selectedMetric}
              setSelectedMetric={setSelectedMetric}
              availableSensors={availableSensors}
              availableMetrics={availableMetrics}
              onAddTrendline={onAddTrendline}
              trendlines={trendlines}
              onRemoveTrendline={onRemoveTrendline}
            />
            <QuickTooltip text="First select a base sensor, then choose which stat you want to analyze the trend for. Click 'Add Trendline' to see the trend.">
              <Info className="h-4 w-4 text-gray-500 hover:text-gray-700 cursor-help" />
            </QuickTooltip>
          </div>
        </div>
      </div>
      {/* Main chart display */}
      <ChartCard
        title={title}
        apiPath={ApiPath.INSTALLATION_GENERIC_LINE_CHART_QUERY}
        yAxisName={chartType === 'power' ? 'Active power' : `Temperature (°C)`}
        unit={chartType === 'power' ? 'watts' : '°C'}
        query={getQuery()}
        trendlineData={convertedTrendlineData}
        combineSeries={getCombineSeries()}
        clampYAxisToData
        hideChartOptions={false}
        canShowTemperatureOnPrimaryYAxis={chartType === 'temperature'}
      />
    </>
  );
} 